import React from "react";
import {
  C, Label, Draw, Worker, workerJoints, workerPose, Arm, ARM_POSES, taper,
  clamp, lerp, seg, span, smooth, ease, mixColor, rng,
} from "../kit";

// Act III · Replaced (the first chorus). A montage of short shots in which
// the cold takes his work and his home: an arm is lowered onto his line; it
// takes the hammer out of his hand and drives the nail itself; it sets the
// last nail in a pine box; the home fields are sold and staked; phones light
// up where the Bible lay; the tomatoes ripen and rot with nobody to pick them.
//
// Shots start at p = .00 line · .10 bench · .30 coffin · .48 field · .665
// table · .835 vine, with a quick dissolve at each cut. Each shot keys its
// story beats to q, its own 0 -> 1; flicker and sway run on seconds.
// Every SVG id starts with "rp-".

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

// Tall phones letterbox far past BLEED, so backgrounds run further still.
const EXT = { x: -400, y: -1800, w: 2400, h: 4500 };
const XF = 0.45; // seconds of dissolve at each cut

const frac = (v) => v - Math.floor(v);
const noise = (n) => frac(Math.sin(n * 127.1 + 311.7) * 43758.5453);
const deg = (r) => (r * 180) / Math.PI;
const f1 = (v) => (Math.round(v * 10) / 10).toString();
const backOut = (k) => 1 + 2.7 * Math.pow(k - 1, 3) + 1.7 * Math.pow(k - 1, 2);
const line = (list) => list.map(([x, y], i) => `${i ? "L" : "M"} ${f1(x)} ${f1(y)}`).join(" ");
const poly = (list) => line(list) + " Z";
const circ = (cx, cy, r) => `M ${f1(cx - r)} ${f1(cy)} a ${r} ${r} 0 1 1 ${2 * r} 0 a ${r} ${r} 0 1 1 ${-2 * r} 0 `;
const rot = ([x, y], a) => [x * Math.cos(a) - y * Math.sin(a), x * Math.sin(a) + y * Math.cos(a)];
const down = ([x, y], h) => [x, y + h];
// A pose { x, y, a, s } carries a local point into the scene.
const at = (pose, [px, py]) => {
  const s = pose.s || 1;
  const [rx, ry] = rot([px * s, py * s], pose.a);
  return [pose.x + rx, pose.y + ry];
};
const tf = (pose) => `translate(${f1(pose.x)} ${f1(pose.y)}) rotate(${deg(pose.a).toFixed(2)}) scale(${pose.s || 1})`;

// Keyed values: [[q, value, easing into this key], ...]; values are numbers
// or poses. Easing defaults to inOut.
function curve(keys, q) {
  if (q <= keys[0][0]) return keys[0][1];
  for (let i = 1; i < keys.length; i++) {
    const [q1, v1, e = ease.inOut] = keys[i];
    if (q <= q1) {
      const [q0, v0] = keys[i - 1];
      const k = e((q - q0) / (q1 - q0));
      if (typeof v0 === "number") return lerp(v0, v1, k);
      return { x: lerp(v0.x, v1.x, k), y: lerp(v0.y, v1.y, k), a: lerp(v0.a, v1.a, k), s: v0.s };
    }
  }
  return keys[keys.length - 1][1];
}

// A decaying shudder `since` seconds after an impact.
function jolt(since, amp, len = 0.5) {
  if (since < 0 || since > len) return [0, 0];
  const d = amp * Math.pow(1 - since / len, 2);
  return [Math.sin(since * 83) * d, Math.cos(since * 61) * d * 0.6];
}

// A round-ended bar between two points: the links of a close-up arm.
function Bar({ a, b, w, ...rest }) {
  const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const ang = deg(Math.atan2(b[1] - a[1], b[0] - a[0]));
  return (
    <rect x={0} y={-w / 2} width={f1(len)} height={w} rx={w / 2}
      transform={`translate(${f1(a[0])} ${f1(a[1])}) rotate(${ang.toFixed(2)})`} {...rest} />
  );
}

// Two-link reach: where the elbow sits for shoulder S and wrist W.
function elbowFor(S, W, L1r, L2r, side = 1) {
  const dx = W[0] - S[0];
  const dy = W[1] - S[1];
  const d = clamp(Math.hypot(dx, dy), Math.abs(L1r - L2r) + 1, L1r + L2r - 1);
  const a = Math.atan2(dy, dx) + side * Math.acos((L1r * L1r + d * d - L2r * L2r) / (2 * L1r * d));
  return [S[0] + L1r * Math.cos(a), S[1] + L1r * Math.sin(a)];
}

// ---------------------------------------------------------------------------
// Shot 1 · The line. The plant from Act I lit cold: a folded arm comes down
// on the hoist beside his station and lands; he stops and looks up while the
// rest of the line keeps hammering.
// ---------------------------------------------------------------------------

// One-point perspective down the line: X lateral and h height in screen
// units at depth Z = 1 (his station); Z grows away from the camera.
const VPX = 1080;
const VPY = 350;
const G = 375; // floor drop below the vanishing point at Z = 1
const S0 = 1.6; // his scale at Z = 1
const P = (X, Z, h = 0) => [VPX + X / Z, VPY + (G - h) / Z];
const WALL_L = -740;
const WALL_R = 900;
const CEIL = 820;
const FAR = 13;
const BELT = { x0: -380, x1: -200, top: 150, under: 100, z0: 0.93 };

const HALL = (() => {
  const seg2 = (a, b) => `M ${f1(a[0])} ${f1(a[1])} L ${f1(b[0])} ${f1(b[1])} `;
  let lines = "";
  for (let X = WALL_L + 100; X < WALL_R; X += 130) lines += seg2(P(X, 0.4), P(X, FAR));
  [0.82, 1.15, 1.6, 2.25, 3.2, 4.6, 6.7, 9.6].forEach((z) => { lines += seg2(P(WALL_L, z), P(WALL_R, z)); });
  let beams = "";
  [1.4, 2.0, 2.8, 3.9, 5.4, 7.6, 10.6].forEach((z) => { beams += seg2(P(WALL_L, z, CEIL - 40), P(WALL_R, z, CEIL - 40)); });
  [-420, -150, 160, 480].forEach((X) => { beams += seg2(P(X, 0.9, CEIL - 40), P(X, FAR, CEIL - 40)); });
  const [bx0, by0] = P(WALL_L, FAR, CEIL);
  const [bx1, by1] = P(WALL_R, FAR);
  return {
    floor: poly([P(WALL_L, FAR), P(WALL_R, FAR), P(WALL_R, 0.15), P(WALL_L, 0.15)]),
    wallL: poly([P(WALL_L, 0.3, CEIL), P(WALL_L, FAR, CEIL), P(WALL_L, FAR), P(WALL_L, 0.3)]),
    wallR: poly([P(WALL_R, 0.3, CEIL), P(WALL_R, FAR, CEIL), P(WALL_R, FAR), P(WALL_R, 0.3)]),
    back: { x: bx0, y: by0, w: bx1 - bx0, h: by1 - by0 },
    lines,
    beams,
    pillars: [1.45, 2.4, 3.8, 5.8, 8.6].map((z) => {
      const [x, y] = P(520, z);
      const top = VPY - (CEIL - G) / z;
      return { x: x - 22 / z, y: top, w: 44 / z, h: y - top };
    }),
    beltTop: poly([P(BELT.x0, BELT.z0, BELT.top), P(BELT.x1, BELT.z0, BELT.top), P(BELT.x1, FAR, BELT.top), P(BELT.x0, FAR, BELT.top)]),
    beltSide: poly([P(BELT.x1, BELT.z0, BELT.top), P(BELT.x1, FAR, BELT.top), P(BELT.x1, FAR, BELT.under), P(BELT.x1, BELT.z0, BELT.under)]),
    beltEnd: poly([P(BELT.x0, BELT.z0, BELT.top), P(BELT.x1, BELT.z0, BELT.top), P(BELT.x1, BELT.z0, BELT.under), P(BELT.x0, BELT.z0, BELT.under)]),
    beltLegs: [0.98, 1.4, 1.95, 2.7, 3.7, 5.1, 7.0, 9.6].map((z) => {
      const a = P(BELT.x1 - 12, z, BELT.under);
      const b = P(BELT.x1 - 12, z, 0);
      const c = P(BELT.x0 + 12, z, BELT.under);
      const e = P(BELT.x0 + 12, z, 0);
      return `M ${f1(a[0])} ${f1(a[1])} V ${f1(b[1])} M ${f1(c[0])} ${f1(c[1])} V ${f1(e[1])}`;
    }).join(" "),
  };
})();

const LAMPS = [
  ...[1.15, 1.6, 2.2, 3.0, 4.1, 5.6, 7.6, 10.2].map((z) => ({ z, X: -290 })),
  ...[1.85, 2.6, 3.6, 5.0, 7.0, 9.6].map((z) => ({ z, X: 400 })),
].map((l) => ({ ...l, k: 1 / l.z }));

const STATIONS = [
  { z: 7.6, X: -470, facing: 1, ph: 2.2 },
  { z: 6.4, X: -110, facing: -1, ph: 0.4 },
  { z: 5.4, X: -470, facing: 1, ph: 4.1 },
  { z: 4.5, X: -110, facing: -1, ph: 1.3 },
  { z: 3.8, X: -470, facing: 1, ph: 5.0 },
  { z: 3.15, X: -110, facing: -1, ph: 2.9 },
  { z: 2.6, X: -470, facing: 1, ph: 0.9 },
  { z: 2.15, X: -110, facing: -1, ph: 3.6 },
  { z: 1.75, X: -470, facing: 1, ph: 1.8 },
];

// A hammer in the near hand of a working man.
function WorkHammer({ x, y, s, facing, phase, color }) {
  const j = workerJoints({ x, y, s, facing, pose: "work", phase });
  const a = workerPose("work", phase).arms[1][1];
  const dx = Math.sin(a) * facing;
  const dy = Math.cos(a);
  const [hx, hy] = j.hand;
  const ex = hx + dx * 22 * s;
  const ey = hy + dy * 22 * s;
  return (
    <path
      d={`M ${f1(hx)} ${f1(hy)} L ${f1(ex)} ${f1(ey)} M ${f1(ex - dy * 7 * s)} ${f1(ey + dx * 7 * s)} L ${f1(ex + dy * 7 * s)} ${f1(ey - dx * 7 * s)}`}
      stroke={color} strokeWidth={4 * s} strokeLinecap="round" fill="none"
    />
  );
}

// The arm unfolds after delivery joint by joint, each with a mechanical snap.
const armUnfold = (k) =>
  ARM_POSES.fold.map((v, i) => lerp(v, ARM_POSES.home[i], backOut(seg(k, i * 0.24, 0.52 + i * 0.24))));

function LineShot({ q, len, local }) {
  const land = 0.55;
  const since = (q - land) * len;
  const [hx, hy] = P(-470, 1);
  const [ax, ay] = P(-470, 1.3);
  const as = S0 / 1.3;
  const armY = ay - 1050 * (1 - ease.out(seg(q, -0.2, land)));
  const boot = seg(q, land + 0.06, land + 0.24);
  const led = boot <= 0 ? 0 : boot < 1 ? (frac(boot * 3.5) < 0.5 ? 1 : 0.1) : 1;
  const armPose = armUnfold(seg(q, land + 0.1, land + 0.42));
  const man = q < 0.42
    ? { pose: "work", phase: local * 7 }
    : q < land + 0.04 ? { pose: "stand", phase: 0 } : { pose: "lookUp", phase: 0 };
  const [jx, jy] = jolt(since, 7);
  const zoom = lerp(1, 1.035, smooth(seg(q, -0.3, 1.2)));
  const cam = `translate(${f1(jx)} ${f1(jy)}) translate(700 520) scale(${zoom.toFixed(4)}) translate(-700 -520)`;

  // The belt steps on by hand pace.
  const L = local / 2.6;
  const shift = Math.floor(L) + ease.inOut(seg(frac(L), 0.7, 1));
  const units = [];
  for (let k = 0; k < 22; k++) {
    const z = 1 + 0.25 * (k - frac(shift));
    if (z < 0.8) continue;
    const [ux, uy] = P(-290, z, BELT.top);
    units.push(<rect key={k} x={f1(ux - 30 / z)} y={f1(uy - 22 / z)} width={f1(60 / z)} height={f1(22 / z)} fill="#5d6e80" opacity={seg(z, 0.84, 0.97)} />);
  }
  let slats = "";
  for (let k = 0; k < 40; k++) {
    const z = BELT.z0 + 0.125 * (k + 1 - frac(shift * 2));
    const [a, b] = [P(BELT.x0, z, BELT.top), P(BELT.x1, z, BELT.top)];
    slats += `M ${f1(a[0])} ${f1(a[1])} L ${f1(b[0])} ${f1(b[1])} `;
  }

  const ink = "#0a0e13";
  const station = (st, i) => {
    const s = S0 / st.z;
    const [x, y] = P(st.X, st.z);
    const phase = local * 7 + st.ph;
    return (
      <g key={i}>
        <Worker x={x} y={y} s={s} facing={st.facing} pose="work" phase={phase} color={ink} />
        <WorkHammer x={x} y={y} s={s} facing={st.facing} phase={phase} color={ink} />
      </g>
    );
  };

  return (
    <g>
      <defs>
        <linearGradient id="rp-ln-floor" gradientUnits="userSpaceOnUse" x1="0" y1={VPY} x2="0" y2="900">
          <stop offset="0" stopColor="#2e3d4b" />
          <stop offset="1" stopColor="#111a22" />
        </linearGradient>
        <linearGradient id="rp-ln-cone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.27" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </linearGradient>
        <radialGradient id="rp-ln-pool">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.38" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g transform={cam}>
        <rect x={EXT.x} y={EXT.y} width={EXT.w} height={EXT.h} fill="#121b25" />
        <path d={HALL.floor} fill="url(#rp-ln-floor)" />
        <path d={HALL.wallL} fill="#16212c" />
        <path d={HALL.wallR} fill="#16212c" />
        <rect x={HALL.back.x} y={HALL.back.y} width={HALL.back.w} height={HALL.back.h} fill="#1a2633" />
        <path d={HALL.lines} stroke="#2c3a47" strokeWidth={1} fill="none" />
        <path d={HALL.beams} stroke="#2c3a47" strokeWidth={2} fill="none" />
        {HALL.pillars.map((pl, i) => <rect key={i} x={pl.x} y={pl.y} width={pl.w} height={pl.h} fill="#16212c" />)}
        {LAMPS.map((l, i) => {
          const [lx, ly] = P(l.X, l.z, 600);
          const fy = VPY + G * l.k;
          return (
            <g key={i} opacity={0.85}>
              <ellipse cx={lx} cy={fy} rx={300 * l.k} ry={64 * l.k} fill="url(#rp-ln-pool)" />
              <path d={`M ${f1(lx - 22 * l.k)} ${f1(ly + 18 * l.k)} L ${f1(lx + 22 * l.k)} ${f1(ly + 18 * l.k)} L ${f1(lx + 250 * l.k)} ${f1(fy)} L ${f1(lx - 250 * l.k)} ${f1(fy)} Z`} fill="url(#rp-ln-cone)" />
            </g>
          );
        })}

        {STATIONS.filter((st) => st.facing === 1).map(station)}
        <path d={HALL.beltLegs} stroke="#141c24" strokeWidth={3} fill="none" />
        <path d={HALL.beltSide} fill="#151d26" />
        <path d={HALL.beltTop} fill="#2a3744" />
        <path d={slats} stroke="#1b242e" strokeWidth={1} fill="none" />
        <path d={HALL.beltEnd} fill="#0e141b" />
        {units}
        {STATIONS.filter((st) => st.facing === -1).map(station)}

        {/* The arm comes down on the hoist beside him */}
        <Arm x={ax} y={armY} s={as} pose={armPose} lit={1} led={led}
          hoist={lerp(1500, 0, seg(q, land + 0.08, land + 0.3))} />
        {man.pose === "work" && <WorkHammer x={hx} y={hy} s={S0} facing={1} phase={man.phase} color="#08090b" />}
        <Worker x={hx} y={hy} s={S0} facing={1} pose={man.pose} phase={man.phase} color="#08090b" />

        {LAMPS.map((l, i) => {
          const [lx, ly] = P(l.X, l.z, 600);
          const top = VPY - (CEIL - G) / l.z;
          return (
            <g key={i}>
              <path d={`M ${f1(lx)} ${f1(top)} V ${f1(ly)}`} stroke="#2c3a47" strokeWidth={Math.max(1, 2 * l.k)} />
              <path d={`M ${f1(lx - 8 * l.k)} ${f1(ly)} L ${f1(lx + 8 * l.k)} ${f1(ly)} L ${f1(lx + 28 * l.k)} ${f1(ly + 18 * l.k)} L ${f1(lx - 28 * l.k)} ${f1(ly + 18 * l.k)} Z`} fill="#1d2731" />
              <ellipse cx={lx} cy={ly + 18 * l.k} rx={22 * l.k} ry={4 * l.k} fill={C.coldHot} />
            </g>
          );
        })}
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Close-up props shared by the bench and the coffin: his hammer, his hand,
// and the arm's gripper reaching in from above.
// ---------------------------------------------------------------------------

// The hammer in its own frame: grip at the origin, the handle along +x and
// the head at the far end, striking face down (+y) and claw up.
const HAMMER_HANDLE = "M -78 -9 Q -86 0 -78 9 L 352 12 L 352 -12 Z";
const HAMMER_HEAD =
  "M 350 -13 L 350 -34 Q 336 -58 302 -84 L 312 -93 Q 352 -74 367 -56 L 394 -56 L 394 40 " +
  "L 401 46 L 401 74 L 343 74 L 343 46 L 350 40 L 350 13 Z";
const FACE = [372, 74];
const GRASP = [372, -22]; // where the gripper's jaws close on the head

// turn: the arm's wrist spins the hammer about the head's own axis, from 0
// (handle to the left) to 1 (handle to the right); the head stays put.
function Hammer({ pose, wood, steel, edge, turn = 0 }) {
  const c = Math.cos(turn * Math.PI);
  const head = Math.sign(c || 1) * Math.max(0.4, Math.abs(c));
  return (
    <g transform={tf(pose)}>
      <g transform={`translate(372 0) scale(${c.toFixed(3)} 1) translate(-372 0)`}>
        <path d={HAMMER_HANDLE} fill={wood} />
        <path d="M -60 -5 L 300 -6" stroke="#000" strokeOpacity={0.25} strokeWidth={2} fill="none" />
      </g>
      <g transform={`translate(372 0) scale(${head.toFixed(3)} 1) translate(-372 0)`}>
        <path d={HAMMER_HEAD} fill={steel} stroke={edge} strokeWidth={1.6} />
        <path d="M 352 -50 L 392 -50" stroke={edge} strokeOpacity={0.5} strokeWidth={2} />
      </g>
    </g>
  );
}

// The gripper in its own frame: jaws centred on the origin, wrist up -y.
// turn spins it about that axis, as Hammer's does.
const GRIP_WRIST = [0, -180];
function Gripper({ pose, gap, body, dark, rim, led, turn = 0 }) {
  const j = gap / 2;
  const w = Math.max(0.45, Math.abs(Math.cos(turn * Math.PI)));
  return (
    <g transform={tf(pose)}>
      <g transform={w < 1 ? `scale(${w.toFixed(3)} 1)` : undefined}>
        <rect x={-j - 17} y={-80} width={17} height={88} rx={3} fill={body} stroke={rim} strokeWidth={1.4} />
        <rect x={j} y={-80} width={17} height={88} rx={3} fill={body} stroke={rim} strokeWidth={1.4} />
        <rect x={-j - 3} y={-48} width={3} height={52} fill={dark} />
        <rect x={j} y={-48} width={3} height={52} fill={dark} />
        <rect x={-70} y={-100} width={140} height={22} rx={3} fill={dark} />
        <rect x={-58} y={-154} width={116} height={58} rx={9} fill={body} stroke={rim} strokeWidth={1.4} />
        <rect x={-46} y={-147} width={92} height={4} rx={2} fill={C.coldHot} opacity={0.35} />
      </g>
      <rect x={-36} y={-180} width={72} height={28} rx={4} fill={dark} />
      <circle cx={40 * w} cy={-124} r={4.5} fill={C.coldHot} opacity={led} />
    </g>
  );
}

// Two links from a shoulder off-frame down to the gripper's wrist.
function Reach({ shoulder, wrist, l1, l2, w1, w2, body, dark, rim }) {
  const E = elbowFor(shoulder, wrist, l1, l2, 1);
  return (
    <g>
      <Bar a={shoulder} b={E} w={w1} fill={body} stroke={rim} strokeWidth={1.5} />
      <Bar a={E} b={wrist} w={w2} fill={body} stroke={rim} strokeWidth={1.5} />
      <circle cx={f1(E[0])} cy={f1(E[1])} r={w1 * 0.66} fill={dark} />
      <circle cx={f1(E[0])} cy={f1(E[1])} r={w1 * 0.24} fill={body} />
      <circle cx={f1(wrist[0])} cy={f1(wrist[1])} r={w2 * 0.62} fill={dark} />
    </g>
  );
}

// His hand from the back, in the hammer's frame, fingers wrapped down over
// the handle. curl 1 is a fist round the handle, 0 an open hand.
const WRIST = [-6, -152];
const DORSUM =
  "M -62 -10 Q -68 -64 -52 -112 Q -46 -140 -40 -164 L 32 -172 Q 46 -138 56 -100 " +
  "Q 64 -60 60 -20 Q 0 -32 -62 -10 Z";
const FINGERS = [
  { x: -45, y: -15, w: 24, len: [36, 22, 18] },
  { x: -17, y: -21, w: 27, len: [43, 27, 21] },
  { x: 11, y: -24, w: 28, len: [46, 29, 22] },
  { x: 39, y: -23, w: 27, len: [43, 26, 20] },
];
const THUMB_CURL = [[50, -104], [80, -54], [70, -14]];
const THUMB_OPEN = [[50, -104], [86, -74], [106, -56]];

function handGeom(curl, thumbOpen) {
  const vis = Math.cos((clamp(curl) * Math.PI) / 2);
  const fingers = FINGERS.map((f, k) => {
    const th = (k - 1.6) * 0.12 * (1 - curl);
    const dx = Math.sin(th);
    const dy = Math.cos(th);
    const l0 = f.len[0] * (1 - 0.1 * curl);
    const p1 = [f.x + dx * l0, f.y + dy * l0];
    const p2 = [p1[0] + dx * f.len[1] * vis, p1[1] + dy * f.len[1] * vis];
    const p3 = [p2[0] + dx * f.len[2] * vis, p2[1] + dy * f.len[2] * vis];
    const d = taper([[f.x, f.y - 6], [f.x, f.y], p1, p2, p3], [f.w, f.w, f.w * 0.96, f.w * 0.9, f.w * 0.84]);
    return { d, w: f.w, base: [f.x, f.y], end: p3 };
  });
  const th = THUMB_CURL.map((c, i) => [lerp(c[0], THUMB_OPEN[i][0], thumbOpen), lerp(c[1], THUMB_OPEN[i][1], thumbOpen)]);
  // Seams between fingers catch a little of the lamp.
  let seams = "";
  for (let k = 0; k < 3; k++) {
    const a = fingers[k];
    const b = fingers[k + 1];
    const mx = (a.base[0] + b.base[0]) / 2;
    const my = (a.base[1] + b.base[1]) / 2;
    const ex = (a.end[0] + b.end[0]) / 2;
    const ey = Math.min(a.end[1], b.end[1]) - 9;
    seams += `M ${f1(mx)} ${f1(my + 4)} L ${f1(ex)} ${f1(ey)} `;
  }
  return { fingers, thumb: taper(th, [34, 29, 25]), seams };
}

function Hand({ pose, curl, thumbOpen, ink, warmK, coldK }) {
  const g = handGeom(curl, thumbOpen);
  const layer = (color, dx, dy, key, opacity = 1) => (
    <g key={key} transform={`translate(${dx} ${dy})`} fill={color} stroke={color}
      strokeLinecap="round" strokeLinejoin="round" opacity={opacity}>
      <path d={DORSUM} stroke="none" />
      <path d={g.thumb} stroke="none" />
      {g.fingers.map((f, i) => <path key={i} d={f.d} stroke="none" />)}
    </g>
  );
  // Big working knuckles, standing up when he grips.
  const knuckles = FINGERS.map((f) => `M ${f.x - 10} ${f.y + 3} Q ${f.x} ${f.y - 7} ${f.x + 10} ${f.y + 3}`).join(" ");
  return (
    <g transform={tf(pose)}>
      {warmK > 0.01 && layer(C.warm, -3, -2.5, "w", 0.85 * warmK)}
      {coldK > 0.01 && layer(C.cold, 3, -2, "c", 0.7 * coldK)}
      {layer(ink, 0, 0, "k")}
      <path d={g.seams} stroke={mixColor(C.warmDeep, C.coldDeep, coldK)} strokeWidth={2} strokeLinecap="round" fill="none" />
      <path d={knuckles} stroke={mixColor(C.warm, C.cold, coldK)} strokeWidth={1.6} strokeLinecap="round" fill="none"
        opacity={(0.18 + 0.3 * curl) * (0.4 + 0.6 * Math.max(warmK, coldK))} />
    </g>
  );
}

// The forearm from his wrist up to an elbow off the top of the frame, in a
// rolled work-shirt sleeve.
function Forearm({ wrist, elbow, ink, warmK, coldK }) {
  const dx = elbow[0] - wrist[0];
  const dy = elbow[1] - wrist[1];
  const L = Math.hypot(dx, dy);
  const n = [-dy / L, dx / L];
  const pt = (k, w) => [wrist[0] + dx * k + n[0] * w, wrist[1] + dy * k + n[1] * w];
  const arm = poly([pt(-0.04, -38), pt(-0.04, 37), pt(0.25, 52), pt(0.56, 55), pt(0.56, -55), pt(0.25, -50)]);
  const shirt = poly([pt(0.62, -62), pt(0.62, 64), pt(2.4, 96), pt(2.4, -94)]);
  const cuff = poly([pt(0.5, -66), pt(0.5, 66), pt(0.65, 70), pt(0.65, -70)]);
  const folds = line([pt(0.55, -67), pt(0.55, 67)]) + " " + line([pt(0.6, -68), pt(0.6, 69)]);
  const sleeve = mixColor("#2a1a11", "#141a20", coldK);
  return (
    <g>
      <g transform="translate(-3 -2.5)" fill={C.warm} opacity={0.85 * warmK}>
        <path d={arm} />
        <path d={cuff} />
        <path d={shirt} />
      </g>
      <path d={shirt} fill={sleeve} />
      <path d={arm} fill={ink} />
      <path d={cuff} fill={sleeve} />
      <path d={folds} stroke="#000" strokeOpacity={0.55} strokeWidth={2.5} fill="none" />
    </g>
  );
}

function GoldWatch({ x, y, shine, cold }) {
  const gold = mixColor("#d6a95a", "#8d97a0", cold * 0.6);
  return (
    <g>
      <path d={`M ${x - 92} ${y + 8} Q ${x - 56} ${y - 6} ${x - 25} ${y} M ${x + 25} ${y} Q ${x + 58} ${y + 10} ${x + 96} ${y + 3}`}
        stroke="#160e08" strokeWidth={11} fill="none" strokeLinecap="round" />
      <ellipse cx={x} cy={y + 2} rx={29} ry={13} fill={mixColor("#6e4a1a", "#2a3038", cold * 0.6)} />
      <ellipse cx={x} cy={y - 1} rx={26} ry={11.5} fill={gold} />
      <ellipse cx={x} cy={y - 1.5} rx={21} ry={9} fill={mixColor("#efe2c2", "#9aa6b0", cold * 0.5)} />
      <path d={`M ${x} ${y - 1.5} L ${x - 2.5} ${y - 8} M ${x} ${y - 1.5} L ${x + 12} ${y + 1}`} stroke="#3a2a14" strokeWidth={1.5} strokeLinecap="round" />
      <ellipse cx={x - 9} cy={y - 7} rx={7} ry={2.2} fill="#fff6e0" opacity={shine} />
    </g>
  );
}

// Light sparks thrown from a strike: [vx, vy] in px/s.
const SPARKS = (() => {
  const r = rng(77);
  return Array.from({ length: 9 }, () => {
    const a = -Math.PI / 2 + (r() - 0.5) * 2.7;
    const v = 260 + r() * 420;
    return [Math.cos(a) * v, Math.sin(a) * v];
  });
})();

function Strike({ x, y, since, scale = 1 }) {
  if (since < 0 || since > 0.55) return null;
  const k = since / 0.55;
  return (
    <g>
      <circle cx={x} cy={y} r={160 * scale} fill="url(#rp-flash)" opacity={Math.pow(1 - k, 2)} />
      {SPARKS.map(([vx, vy], i) => {
        const px = x + vx * since * scale;
        const py = y + (vy * since + 900 * since * since) * scale;
        return (
          <line key={i} x1={f1(px)} y1={f1(py)} x2={f1(px - vx * 0.035 * scale)} y2={f1(py - (vy + 1800 * since) * 0.035 * scale)}
            stroke={C.coldHot} strokeWidth={2 * scale + 0.5} strokeLinecap="round" opacity={1 - k} />
        );
      })}
    </g>
  );
}

const FLASH_DEF = (
  <radialGradient id="rp-flash">
    <stop offset="0" stopColor={C.coldHot} stopOpacity="0.95" />
    <stop offset="0.22" stopColor={C.cold} stopOpacity="0.35" />
    <stop offset="1" stopColor={C.cold} stopOpacity="0" />
  </radialGradient>
);

// ---------------------------------------------------------------------------
// Shot 2 · The bench. His hand taps a nail into a pine board. A gripper
// comes down, clamps the hammer's head, slides the handle out of his fist
// and drives the nail home in one stroke. The hand is left open and empty.
// ---------------------------------------------------------------------------

const BN = { nailX: 1010, plankY: 612, elbow: [412, -350], shoulder: [1900, -700] };
const STRIKE = 0.615;

const BN_BOKEH = [
  { x: 190, y: 200, r: 74, warm: true }, { x: 430, y: 330, r: 44, warm: true }, { x: 70, y: 430, r: 36, warm: true },
  { x: 1190, y: 170, r: 62 }, { x: 1400, y: 300, r: 50 }, { x: 1560, y: 120, r: 84 }, { x: 1290, y: 440, r: 32 },
];
const BN_GRAIN = (() => {
  const r = rng(303);
  let d = "";
  for (let i = 0; i < 9; i++) {
    let y = 574 + i * 14.5 + r() * 6;
    let x = -400;
    d += `M ${x} ${f1(y)}`;
    while (x < 2000) {
      const nx = x + 120 + r() * 170;
      const ny = y + (r() - 0.5) * 3;
      d += ` Q ${f1((x + nx) / 2)} ${f1(y + (r() - 0.5) * 6)} ${f1(nx)} ${f1(ny)}`;
      x = nx;
      y = ny;
    }
  }
  return d;
})();
const BN_DENTS = [[180, 690, 8], [262, 598, 6], [520, 680, 9], [1330, 690, 7], [1480, 588, 5], [880, 692, 6], [612, 584, 5], [1150, 676, 4]];
const PLANK_TOP = poly([[540, 598], [2000, 598], [2000, 627], [526, 627]]);
const PLANK_FRONT = poly([[526, 627], [2000, 627], [2000, 651], [526, 651]]);
const PLANK_GRAIN = "M 560 606 Q 760 602 980 607 T 1500 604 T 2000 606 M 548 619 Q 820 616 1100 621 T 2000 618";

// Where his hand holds the hammer before the arm takes it: two taps, then the
// hammer rests on the nail.
function benchHold(q) {
  const target = q < 0.15 ? lerp(56, 44, seg(q, 0.07, 0.105)) : 44;
  const w = curve([
    [0, -0.3], [0.035, -0.34], [0.07, 0, ease.in], [0.105, -0.36, ease.out], [0.115, -0.36],
    [0.15, 0, ease.in], [0.168, -0.025, ease.out], [0.19, 0],
  ], q);
  const g0 = [BN.nailX - FACE[0], BN.plankY - target - FACE[1]];
  const W = [g0[0] + WRIST[0] + w * 30, g0[1] + WRIST[1] + w * 70];
  const r = rot(WRIST, w);
  return { x: W[0] - r[0], y: W[1] - r[1], a: w };
}

function BenchShot({ q, len, local }) {
  const nailH = q < 0.07 ? 70 : q < 0.15 ? 56 : q < STRIKE ? 44 : 0;
  const grabbed = q >= 0.25;
  const K0 = benchHold(0.25);
  const hold = grabbed ? K0 : benchHold(q);

  // The hammer: in his hand, then on the arm's tool path: out along the
  // handle, up clear of him, turned end for end so the handle points away,
  // then one stroke.
  const above = { x: BN.nailX - FACE[0], y: BN.plankY - 44 - FACE[1] - 210, a: 0 };
  const home = { x: BN.nailX - FACE[0], y: BN.plankY - FACE[1], a: 0 };
  const out = { x: K0.x + 215, y: K0.y, a: 0 };
  const hammer = !grabbed ? hold : curve([
    [0.31, K0],
    [0.4, out],
    [0.46, { ...out, y: out.y - 250 }],
    [0.53, { ...above, x: above.x + 70, y: above.y - 30 }],
    [0.565, above],
    [0.605, { ...above, y: above.y - 40 }],
    [STRIKE, home, ease.in],
    [0.72, home],
    [0.9, { x: home.x + 150, y: home.y - 1000, a: 0 }, ease.in],
  ], q);
  const turn = ease.inOut(seg(q, 0.455, 0.53));

  // His hand opens as the handle slides out, sags, and late on closes a
  // little on nothing.
  const pulled = grabbed ? hammer.x - K0.x : 0;
  const late = smooth(seg(q, 0.8, 1));
  const curl = q < 0.4
    ? 1 - 0.66 * smooth(seg(pulled, 30, 165))
    : lerp(0.34, 0.5, late) + 0.012 * Math.sin(local * 11);
  const thumbOpen = q < 0.4 ? smooth(seg(pulled, 10, 140)) : 1 - 0.3 * late;
  const strain = Math.sin(local * 40) * 1.8 * span(q, 0.255, 0.265, 0.295, 0.31);
  const sag = smooth(seg(q, 0.4, 0.62));
  const hand = {
    x: hold.x + strain + 6 * sag,
    y: hold.y + 18 * sag + 60 * late,
    a: hold.a - 0.06 * sag + 0.04 * late,
  };
  const wrist = at(hand, WRIST);

  // The gripper drops in on a fast, decelerating path and clamps the head.
  const graspAt = at(K0, GRASP);
  let grip = null;
  if (grabbed) {
    const g = at(hammer, GRASP);
    grip = { x: g[0], y: g[1], a: hammer.a };
  } else if (q > 0.16) {
    const k1 = ease.out(seg(q, 0.16, 0.232));
    const k2 = ease.inOut(seg(q, 0.232, 0.247));
    const hx = graspAt[0];
    const hy = graspAt[1] - 64;
    grip = {
      x: lerp(lerp(hx + 180, hx, k1), graspAt[0], k2),
      y: lerp(lerp(hy - 1000, hy, k1), graspAt[1], k2),
      a: 0,
    };
  }
  const gap = lerp(82, 44, ease.in(seg(q, 0.247, 0.252)));
  const led = q < 0.25 ? 0.35 : q < 0.6 ? 1 : 0.6 + 0.4 * (frac(local * 1.5) < 0.5 ? 1 : 0);

  // Light: his warm work lamp gives way to the machine's cold.
  const warmK = 1 - 0.85 * smooth(seg(q, 0.3, 0.8));
  const coldK = smooth(seg(q, 0.14, 0.6));
  const since = (q - STRIKE) * len;
  const [jx, jy] = jolt(since, 10, 0.45);
  const [kx, ky] = jolt((q - 0.25) * len, 3, 0.25);
  const zoom = lerp(1.2, 1.27, smooth(clamp(q)));
  const cam = `translate(${f1(jx + kx)} ${f1(jy + ky)}) translate(820 470) scale(${zoom.toFixed(4)}) translate(-820 -470)`;

  const steel = { body: mixColor("#55616c", "#8d9aa7", coldK), dark: "#10151a", rim: mixColor("#3a4652", C.cold, coldK) };
  const ink = "#0b0806";

  return (
    <g>
      <defs>
        <linearGradient id="rp-bn-wall" gradientUnits="userSpaceOnUse" x1="0" y1="-200" x2="0" y2="570">
          <stop offset="0" stopColor="#030405" />
          <stop offset="1" stopColor={mixColor("#1c140d", "#0e151c", coldK)} />
        </linearGradient>
        <radialGradient id="rp-bn-warm">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.55" />
          <stop offset="0.5" stopColor={C.warm} stopOpacity="0.16" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rp-bn-cold">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.42" />
          <stop offset="0.55" stopColor={C.cold} stopOpacity="0.12" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rp-bn-ww">
          <stop offset="0" stopColor={C.warm} stopOpacity="0.3" />
          <stop offset="0.6" stopColor={C.warmDeep} stopOpacity="0.14" />
          <stop offset="1" stopColor={C.warmDeep} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rp-bn-cw">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.24" />
          <stop offset="0.6" stopColor={C.accent} stopOpacity="0.1" />
          <stop offset="1" stopColor={C.accent} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rp-bn-bw">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.5" />
          <stop offset="0.7" stopColor={C.warm} stopOpacity="0.16" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rp-bn-bc">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.45" />
          <stop offset="0.7" stopColor={C.cold} stopOpacity="0.14" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rp-bn-plank" gradientUnits="userSpaceOnUse" x1="300" y1="0" x2="1700" y2="0">
          <stop offset="0" stopColor={mixColor("#c99459", "#7d8792", coldK * 0.55)} />
          <stop offset="1" stopColor={mixColor("#5a3b22", "#343f4a", coldK * 0.6)} />
        </linearGradient>
        {FLASH_DEF}
      </defs>
      <g transform={cam}>
        <rect x={EXT.x} y={EXT.y} width={EXT.w} height={EXT.h} fill="url(#rp-bn-wall)" />
        <ellipse cx={520} cy={330} rx={620} ry={430} fill="url(#rp-bn-ww)" opacity={warmK} />
        <ellipse cx={1240} cy={260} rx={700} ry={460} fill="url(#rp-bn-cw)" opacity={coldK} />
        {BN_BOKEH.map((b, i) => (
          <circle key={i} cx={b.x} cy={b.y} r={b.r} fill={b.warm ? "url(#rp-bn-bw)" : "url(#rp-bn-bc)"}
            opacity={b.warm ? 0.85 * warmK : 0.2 + 0.7 * coldK} />
        ))}

        {/* The bench, thirty years of dents in it */}
        <rect x={-400} y={566} width={2400} height={140} fill={mixColor("#3c2817", "#1a2027", coldK * 0.7)} />
        <path d={BN_GRAIN} stroke="#000" strokeOpacity={0.32} strokeWidth={1.2} fill="none" />
        {BN_DENTS.map(([x, y, r], i) => <ellipse key={i} cx={x} cy={y} rx={r} ry={r * 0.32} fill="#000" opacity={0.45} />)}
        <ellipse cx={360} cy={652} rx={700} ry={160} fill="url(#rp-bn-warm)" opacity={warmK} />
        <ellipse cx={1200} cy={640} rx={740} ry={150} fill="url(#rp-bn-cold)" opacity={coldK} />
        <rect x={-400} y={704} width={2400} height={54} fill={mixColor("#1f140b", "#0b0f13", coldK * 0.7)} />
        <rect x={-400} y={704} width={2400} height={2} fill={C.warm} opacity={0.35 * warmK} />
        <rect x={-400} y={756} width={2400} height={1960} fill="#040302" />

        {/* The board and the nail */}
        <path d={PLANK_FRONT} fill={mixColor("#4d321c", "#1f262d", coldK * 0.6)} />
        <path d={PLANK_TOP} fill="url(#rp-bn-plank)" />
        <path d={PLANK_GRAIN} stroke="#2a1a0c" strokeOpacity={0.35} strokeWidth={1.2} fill="none" />
        <ellipse cx={712} cy={612} rx={8} ry={2.6} fill="#2a2420" />
        <ellipse cx={858} cy={613} rx={8} ry={2.6} fill="#2a2420" />
        {nailH > 0 && (
          <g>
            <path d={`M ${BN.nailX + 4} ${BN.plankY} L ${BN.nailX + 4 + nailH * 0.5} ${BN.plankY + 3}`} stroke="#000" strokeOpacity={0.4} strokeWidth={4} />
            <rect x={BN.nailX - 3.5} y={BN.plankY - nailH} width={7} height={nailH} fill="#7d878f" />
            <rect x={BN.nailX - 3.5} y={BN.plankY - nailH} width={2} height={nailH} fill="#c4ccd2" opacity={0.6} />
          </g>
        )}
        <ellipse cx={BN.nailX} cy={BN.plankY - nailH} rx={13} ry={4} fill={nailH > 0 ? "#aeb8c0" : "#8a949c"} />
        <GoldWatch x={300} y={660} shine={0.9 * warmK} cold={coldK} />

        {/* His arm, the hammer, his hand */}
        <Hammer pose={hammer} turn={turn} wood={mixColor("#8a5c33", "#4b4038", coldK * 0.5)} steel={steel.body} edge={steel.rim} />
        <Forearm wrist={wrist} elbow={BN.elbow} ink={ink} warmK={warmK} coldK={coldK} />
        <Hand pose={hand} curl={curl} thumbOpen={thumbOpen} ink={ink} warmK={warmK} coldK={coldK} />

        {/* The arm reaches in */}
        {grip && (
          <g>
            <Reach shoulder={BN.shoulder} wrist={at(grip, GRIP_WRIST)} l1={760} l2={640} w1={112} w2={80} {...steel} />
            <Gripper pose={grip} gap={gap} led={led} turn={turn} {...steel} />
          </g>
        )}
        <Strike x={BN.nailX} y={BN.plankY} since={since} />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Shot 3 · The pine box. Under one cold lamp the lid slides shut, then the
// arm brings the hammer down on the last nail.
// ---------------------------------------------------------------------------

const CF = { cx: 790, cy: 440, A: 310, B: 84, H: 84, T: 14 };
// Plan outline: head end left, shoulders, foot end right; u along, v across.
const CF_PLAN = [[-1, -0.6], [-0.56, -1], [1, -0.52], [1, 0.52], [-0.56, 1], [-1, 0.6]];
const cfp = (u, v, dx = 0, dy = 0, k = 1) => [CF.cx + u * CF.A * k + dx, CF.cy + v * CF.B * k + dy];
const CF_RIM = CF_PLAN.map(([u, v]) => cfp(u, v));
const CF_INNER = CF_PLAN.map(([u, v]) => cfp(u * 0.965, v * 0.84));
const CF_FRONT_EDGES = [[5, 4], [4, 3]];
const CF_SIDES = CF_FRONT_EDGES.map(([i, j]) => poly([CF_RIM[i], CF_RIM[j], down(CF_RIM[j], CF.H), down(CF_RIM[i], CF.H)]));
const CF_SEAMS = CF_FRONT_EDGES.map(([i, j]) => [1 / 3, 2 / 3].map((k) => line([down(CF_RIM[i], CF.H * k), down(CF_RIM[j], CF.H * k)])).join(" ")).join(" ");
const CF_BACKWALLS = [[0, 1], [1, 2]].map(([i, j]) => poly([CF_INNER[i], CF_INNER[j], down(CF_INNER[j], CF.H * 0.86), down(CF_INNER[i], CF.H * 0.86)]));
const CF_NAILS = [[-0.9, -0.46], [-0.9, 0.46], [-0.52, -0.84], [-0.52, 0.84], [-0.05, -0.76], [-0.05, 0.76], [0.42, -0.66], [0.42, 0.66], [0.9, -0.38]];
const CF_LAST = [0.9, 0.36];
const CF_LID_GRAIN = (() => {
  const r = rng(808);
  let d = "";
  for (let i = 0; i < 7; i++) {
    const v = -0.8 + i * 0.27 + r() * 0.08;
    const u0 = -0.95 + r() * 0.3;
    const u1 = 0.5 + r() * 0.45;
    d += `M ${f1(u0 * CF.A)} ${f1(v * CF.B)} Q ${f1(((u0 + u1) / 2) * CF.A)} ${f1((v + (r() - 0.5) * 0.12) * CF.B)} ${f1(u1 * CF.A)} ${f1(v * CF.B)} `;
  }
  return d;
})();

function Sawhorse({ x, top, floor, ink }) {
  return (
    <g stroke={ink} strokeLinecap="round" fill="none">
      <path d={`M ${x - 70} ${top} H ${x + 70}`} strokeWidth={14} />
      <path d={`M ${x - 46} ${top} L ${x - 92} ${floor} M ${x + 46} ${top} L ${x + 92} ${floor}`} strokeWidth={11} />
      <path d={`M ${x - 72} ${(top + floor) / 2 + 20} H ${x + 72}`} strokeWidth={6} />
    </g>
  );
}

function CoffinShot({ q, len }) {
  const slide = -290 * (1 - ease.inOut(seg(q, 0.04, 0.38)));
  const shut = (q - 0.38) * len;
  const hs = 0.8;
  const nail = cfp(CF_LAST[0], CF_LAST[1], slide, -CF.T, 1.035);
  const nailUp = q < 0.665 ? 20 : 0;
  const home = { x: nail[0] - FACE[0] * hs, y: nail[1] - FACE[1] * hs, a: 0, s: hs };
  const above = { ...home, y: home.y - 20 - 110 };
  const hammer = curve([
    [0.42, { ...above, x: above.x + 560, y: above.y - 900 }],
    [0.58, above, ease.out],
    [0.65, { ...above, y: above.y - 28 }],
    [0.665, home, ease.in],
    [0.78, home],
    [0.95, { ...home, x: home.x + 320, y: home.y - 940 }, ease.in],
  ], q);
  const g = at(hammer, GRASP);
  const grip = { x: g[0], y: g[1], a: 0, s: hs };
  const since = (q - 0.665) * len;
  const [jx, jy] = jolt(shut, 5, 0.4);
  const [kx, ky] = jolt(since, 8, 0.45);
  const zoom = lerp(1, 1.06, smooth(clamp(q)));
  const cam = `translate(${f1(jx + kx)} ${f1(jy + ky)}) translate(800 470) scale(${zoom.toFixed(4)}) translate(-800 -470)`;

  const cool = 0.2 + 0.5 * smooth(seg(q, 0.55, 1));
  const lamp = 1 - 0.55 * smooth(seg(q, 0.8, 1));
  const top = mixColor("#b8854f", "#8ea0b0", cool);
  const side = mixColor("#5c3b1e", "#1c2731", cool);
  const rimTop = mixColor("#8f6439", "#5b6874", cool);
  const lid = CF_PLAN.map(([u, v]) => cfp(u, v, slide, -CF.T, 1.035));
  const lidSides = CF_FRONT_EDGES.map(([i, j]) => poly([lid[i], lid[j], down(lid[j], CF.T), down(lid[i], CF.T)]));
  const seams = [-0.34, 0.34].map((v) => line([cfp(-1, v, slide, -CF.T, 1.035), cfp(1, v, slide, -CF.T, 1.035)])).join(" ");
  const steel = { body: "#8e9ba8", dark: "#10151a", rim: C.cold };
  const floorY = 770;

  return (
    <g>
      <defs>
        <linearGradient id="rp-cf-bg" gradientUnits="userSpaceOnUse" x1="0" y1="-300" x2="0" y2="900">
          <stop offset="0" stopColor="#020304" />
          <stop offset="0.7" stopColor="#0a0e13" />
          <stop offset="1" stopColor="#06080a" />
        </linearGradient>
        <linearGradient id="rp-cf-cone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.3" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0.02" />
        </linearGradient>
        <radialGradient id="rp-cf-pool">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.35" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rp-cf-in" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1a120b" />
          <stop offset="1" stopColor="#030202" />
        </linearGradient>
        <clipPath id="rp-cf-cavity">
          <path d={poly(CF_INNER)} />
        </clipPath>
        {FLASH_DEF}
      </defs>
      <g transform={cam}>
        <rect x={EXT.x} y={EXT.y} width={EXT.w} height={EXT.h} fill="url(#rp-cf-bg)" />
        <rect x={-400} y={floorY - 70} width={2400} height={2000} fill="#05070a" />
        <ellipse cx={800} cy={floorY - 20} rx={660} ry={96} fill="url(#rp-cf-pool)" opacity={lamp} />
        <path d={`M 770 104 L 830 104 L 1330 ${floorY} L 270 ${floorY} Z`} fill="url(#rp-cf-cone)" opacity={0.8 * lamp} />
        <path d={`M 800 -1800 V 70`} stroke="#1b232b" strokeWidth={3} />
        <path d="M 784 70 H 816 L 846 104 H 754 Z" fill="#141b22" />
        <ellipse cx={800} cy={104} rx={40} ry={6} fill={C.coldHot} opacity={0.4 + 0.6 * lamp} />

        <ellipse cx={800} cy={floorY - 34} rx={420} ry={36} fill="#000" opacity={0.55} />
        <Sawhorse x={598} top={600} floor={floorY + 40} ink="#07090b" />
        <Sawhorse x={975} top={586} floor={floorY + 40} ink="#07090b" />

        {/* The box: wall tops, the dark inside, the front boards */}
        <path d={poly(CF_RIM)} fill={rimTop} />
        <path d={poly(CF_INNER)} fill="url(#rp-cf-in)" />
        <g clipPath="url(#rp-cf-cavity)">
          {CF_BACKWALLS.map((d, i) => <path key={i} d={d} fill={mixColor("#2a1b0e", "#111820", cool)} />)}
        </g>
        {CF_SIDES.map((d, i) => <path key={i} d={d} fill={side} />)}
        <path d={CF_SEAMS} stroke="#000" strokeOpacity={0.5} strokeWidth={1.4} fill="none" />
        <path d={line([CF_RIM[5], CF_RIM[4], CF_RIM[3]])} stroke={mixColor("#d9a66b", C.coldHot, cool)} strokeOpacity={0.35} strokeWidth={1.5} fill="none" />

        {/* The lid slides home */}
        {lidSides.map((d, i) => <path key={i} d={d} fill={mixColor("#6b4524", "#26313b", cool)} />)}
        <path d={poly(lid)} fill={top} />
        <path d={seams} stroke="#2a1a0c" strokeOpacity={0.55} strokeWidth={1.5} fill="none" />
        <path d={CF_LID_GRAIN} transform={`translate(${f1(CF.cx + slide)} ${CF.cy - CF.T})`} stroke="#3a2412" strokeOpacity={0.28} strokeWidth={1.2} fill="none" />
        {CF_NAILS.map(([u, v], i) => {
          const [nx, ny] = cfp(u, v, slide, -CF.T, 1.035);
          return <ellipse key={i} cx={f1(nx)} cy={f1(ny)} rx={4} ry={1.8} fill="#2a2622" />;
        })}
        {nailUp > 0 && <rect x={nail[0] - 2.2} y={nail[1] - nailUp} width={4.4} height={nailUp} fill="#7d878f" />}
        <ellipse cx={nail[0]} cy={nail[1] - nailUp} rx={5.5} ry={2} fill={nailUp > 0 ? "#aeb8c0" : "#2a2622"} />

        {/* The arm and the hammer */}
        <Hammer pose={hammer} wood="#6e4c30" steel={steel.body} edge={steel.rim} />
        <Reach shoulder={[1950, -600]} wrist={at(grip, GRIP_WRIST)} l1={680} l2={560} w1={92} w2={64} {...steel} />
        <Gripper pose={grip} gap={44} led={1} {...steel} />
        <Strike x={nail[0]} y={nail[1]} since={since} scale={0.8} />
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Shot 4 · The land. Dusk on the home fields: a SOLD sign is driven in with
// a thump, then survey stakes and string fence the furrows off.
// ---------------------------------------------------------------------------

const FD = { hz: 505, vpx: 860, cam: 300 };
const gpt = (X, Z) => [FD.vpx + X / Z, FD.hz + FD.cam / Z];
const FD_FURROWS = (() => {
  let d = "";
  for (let i = -30; i <= 30; i++) {
    const a = gpt(i * 64, 0.45);
    const b = gpt(i * 64, 60);
    d += `M ${f1(a[0])} ${f1(a[1])} L ${f1(b[0])} ${f1(b[1])} `;
  }
  return d;
})();
const FD_ROWS = [1.15, 1.5, 2, 2.7, 3.7, 5.2, 7.5, 11, 17].map((Z) => `M -400 ${f1(FD.hz + FD.cam / Z)} H 2000`).join(" ");
const FD_FARM = [
  "M 352 507 V 468 L 386 442 L 420 468 V 507 Z",
  "M 420 507 V 482 L 450 482 V 507 Z",
  "M 400 455 V 438 H 408 V 461 Z",
  "M 474 507 V 478 L 484 463 L 505 455 L 526 463 L 536 478 V 507 Z",
  "M 292 507 L 298 448 H 304 L 310 507 Z",
  circ(250, 470, 22), circ(232, 482, 17), circ(268, 484, 18), "M 247 484 H 253 V 507 H 247 Z",
  circ(588, 488, 12), circ(600, 494, 10),
].join(" ");
const STAKES = [
  ...[1.22, 1.5, 1.86, 2.36, 3.0, 4.0, 5.4, 7.4, 10.2].map((Z, i) => ({ X: -560, Z, at: 0.3 + i * 0.03 })),
  ...[-180, 200, 580, 960, 1340, 1720, 2100].map((X, i) => ({ X, Z: 3.0, at: 0.6 + i * 0.028 })),
];
const LINE_A = STAKES.slice(0, 9);
const LINE_B = [STAKES[4], ...STAKES.slice(9)];
const DUST = (() => {
  const r = rng(1919);
  return Array.from({ length: 9 }, () => ({ vx: (r() - 0.5) * 260, vy: -40 - r() * 90, r: 6 + r() * 10 }));
})();
const SIGN = { x: 1240, ground: 762, top: 430, arm: 1026, hook: [1052, 1212], panel: { x: 1036, y: 458, w: 196, h: 128 } };

function Stake({ X, Z, k, local, ink, tape }) {
  if (k <= 0) return null;
  const [x, y] = gpt(X, Z);
  const s = 1 / Z;
  const h = 200 * s;
  const lift = (1 - ease.in(clamp(k * 1.25))) * 80 * s - Math.sin(clamp((k - 0.8) / 0.2) * Math.PI) * 5 * s;
  const tp = y - h + lift;
  const w = Math.max(1.6, 11 * s);
  const fl = Math.sin(local * 5 + X * 0.01 + Z) * 8 * s;
  return (
    <g opacity={Math.min(1, k * 4)}>
      <rect x={f1(x - w / 2)} y={f1(tp)} width={f1(w)} height={f1(h)} fill={ink} />
      <rect x={f1(x - w / 2)} y={f1(tp)} width={f1(w)} height={f1(14 * s)} fill={tape} />
      <path d={`M ${f1(x + w / 2)} ${f1(tp + 4 * s)} Q ${f1(x + 20 * s)} ${f1(tp + fl)} ${f1(x + 40 * s)} ${f1(tp + 6 * s + fl * 1.4)} L ${f1(x + 38 * s)} ${f1(tp + 15 * s + fl * 1.4)} Q ${f1(x + 20 * s)} ${f1(tp + 10 * s + fl)} ${f1(x + w / 2)} ${f1(tp + 13 * s)} Z`} fill={tape} />
    </g>
  );
}

function FieldShot({ q, len, local }) {
  const fall = seg(q, 0.1, 0.13);
  const hit = (q - 0.13) * len;
  const [jx, jy] = jolt(hit, 12, 0.5);
  const swing = hit > 0 ? 0.16 * Math.exp(-hit * 2.2) * Math.sin(hit * 8.5) : 0;
  const signY = -820 * (1 - ease.in(fall));
  const dusk = smooth(seg(q, 0, 1));
  const sky = [
    mixColor("#25223a", "#04060a", dusk),
    mixColor("#55374a", "#0a0f17", dusk),
    mixColor("#b0623c", "#16212d", dusk),
    mixColor("#eaa45c", "#2c3d4d", dusk),
  ];
  const ground = mixColor("#2e1d12", "#0b1016", dusk);
  const groundNear = mixColor("#100a06", "#030507", dusk);
  const furrow = mixColor("#6a4128", "#2a3c4e", dusk);
  const ink = mixColor("#140c08", "#020304", dusk);
  const sunY = lerp(470, 540, ease.inOut(seg(q, 0, 0.75)));
  const windowOn = [q < 0.84 ? 1 : 0, q < 0.9 ? 1 : 0];
  const zoom = lerp(1, 1.04, smooth(clamp(q)));
  const cam = `translate(${f1(jx)} ${f1(jy)}) translate(860 560) scale(${zoom.toFixed(4)}) translate(-860 -560)`;
  const k = (s) => seg(q, s.at, s.at + 0.03);
  const strings = (list) => {
    const out = [];
    for (let i = 1; i < list.length; i++) {
      const a = list[i - 1];
      const b = list[i];
      const draw = seg(q, b.at + 0.012, b.at + 0.045);
      if (draw <= 0) continue;
      [0.35, 0.7].forEach((hk, j) => {
        const pa = gpt(a.X, a.Z);
        const pb = gpt(b.X, b.Z);
        out.push(
          <Draw key={`${i}-${j}`} d={`M ${f1(pa[0])} ${f1(pa[1] - 200 * hk / a.Z)} L ${f1(pb[0])} ${f1(pb[1] - 200 * hk / b.Z)}`}
            p={draw} stroke={mixColor("#d8d2c4", C.cold, dusk)} strokeOpacity={0.6} strokeWidth={1.2} />,
        );
      });
    }
    return out;
  };
  const far = STAKES.filter((s) => s.Z >= 3).sort((a, b) => b.Z - a.Z);
  const near = STAKES.filter((s) => s.Z < 3).sort((a, b) => b.Z - a.Z);
  const tape = C.accent;
  const pc = { x: (SIGN.hook[0] + SIGN.hook[1]) / 2, y: SIGN.top + 6 };

  return (
    <g>
      <defs>
        <linearGradient id="rp-fd-sky" gradientUnits="userSpaceOnUse" x1="0" y1="-200" x2="0" y2={FD.hz}>
          {sky.map((c, i) => <stop key={i} offset={[0, 0.5, 0.84, 1][i]} stopColor={c} />)}
        </linearGradient>
        <linearGradient id="rp-fd-ground" gradientUnits="userSpaceOnUse" x1="0" y1={FD.hz} x2="0" y2="900">
          <stop offset="0" stopColor={ground} />
          <stop offset="1" stopColor={groundNear} />
        </linearGradient>
        <linearGradient id="rp-fd-furrow" gradientUnits="userSpaceOnUse" x1="0" y1={FD.hz} x2="0" y2="900">
          <stop offset="0" stopColor={furrow} stopOpacity="0.8" />
          <stop offset="0.5" stopColor={furrow} stopOpacity="0.35" />
          <stop offset="1" stopColor={furrow} stopOpacity="0.1" />
        </linearGradient>
        <radialGradient id="rp-fd-sun">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.85" />
          <stop offset="0.25" stopColor={C.warm} stopOpacity="0.4" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g transform={cam}>
        <rect x={EXT.x} y={EXT.y} width={EXT.w} height={FD.hz - EXT.y + 2} fill="url(#rp-fd-sky)" />
        <ellipse cx={560} cy={FD.hz} rx={640} ry={230} fill="url(#rp-fd-sun)" opacity={1 - dusk} />
        <circle cx={560} cy={sunY} r={38} fill={mixColor(C.warmHot, C.warm, dusk)} opacity={1 - smooth(seg(q, 0.5, 0.9))} />

        {/* The homeplace on the skyline */}
        <g transform={`translate(420 ${FD.hz + 2}) scale(1.6) translate(-420 ${-FD.hz - 2})`}>
          <path d={FD_FARM} fill={ink} />
          <g transform={`translate(301 446) rotate(${f1(local * 40)})`} stroke={ink} strokeWidth={2}>
            <path d="M -14 0 H 14 M 0 -14 V 14 M -10 -10 L 10 10 M -10 10 L 10 -10" />
          </g>
          <rect x={364} y={476} width={7} height={8} fill={C.warmHot} opacity={0.9 * windowOn[0]} />
          <rect x={398} y={476} width={7} height={8} fill={C.warm} opacity={0.8 * windowOn[1]} />
        </g>

        <rect x={EXT.x} y={FD.hz} width={EXT.w} height={2400} fill="url(#rp-fd-ground)" />
        <path d={FD_FURROWS} stroke="url(#rp-fd-furrow)" strokeWidth={1.2} fill="none" />
        <path d={FD_ROWS} stroke={furrow} strokeOpacity={0.3} strokeWidth={0.8} fill="none" />

        {/* Survey stakes and string */}
        {strings(LINE_B)}
        {far.map((s, i) => <Stake key={`f${i}`} {...s} k={k(s)} local={local} ink={ink} tape={tape} />)}
        {strings(LINE_A)}
        {near.map((s, i) => <Stake key={`n${i}`} {...s} k={k(s)} local={local} ink={ink} tape={tape} />)}

        {/* The sign goes in */}
        {hit > 0 && hit < 1.2 && DUST.map((d, i) => {
          const tt = hit;
          return (
            <circle key={i} cx={f1(SIGN.x + d.vx * tt * (1 - tt * 0.3))} cy={f1(SIGN.ground - 6 + d.vy * tt + 60 * tt * tt)}
              r={d.r * (0.6 + tt)} fill={mixColor("#8a6a4a", "#5a6670", dusk)} opacity={0.35 * (1 - tt / 1.2)} />
          );
        })}
        <g transform={`translate(0 ${f1(signY)})`}>
          <rect x={SIGN.x - 6} y={SIGN.top} width={12} height={SIGN.ground - SIGN.top + 4} fill={ink} />
          <rect x={SIGN.arm} y={SIGN.top + 2} width={SIGN.x - SIGN.arm} height={9} fill={ink} />
          <g transform={`rotate(${f1(deg(swing))} ${pc.x} ${pc.y})`}>
            <path d={`M ${SIGN.hook[0]} ${SIGN.top + 10} V ${SIGN.panel.y} M ${SIGN.hook[1]} ${SIGN.top + 10} V ${SIGN.panel.y}`} stroke={ink} strokeWidth={2.5} />
            <rect x={SIGN.panel.x} y={SIGN.panel.y} width={SIGN.panel.w} height={SIGN.panel.h} fill={mixColor("#e3ddd0", "#a9b6c2", dusk)} stroke={ink} strokeWidth={3} />
            <Label x={SIGN.panel.x + SIGN.panel.w / 2} y={SIGN.panel.y + 74} size={56} anchor="middle" weight={500} spacing={0.1} color={mixColor("#2a1d16", "#0c1218", dusk)}>
              SOLD
            </Label>
            <line x1={SIGN.panel.x + 18} x2={SIGN.panel.x + SIGN.panel.w - 18} y1={SIGN.panel.y + 92} y2={SIGN.panel.y + 92} stroke={mixColor("#2a1d16", "#0c1218", dusk)} strokeOpacity={0.5} />
            <Label x={SIGN.panel.x + SIGN.panel.w / 2} y={SIGN.panel.y + 112} size={11} anchor="middle" spacing={0.16} color={mixColor("#4a3a2e", "#26323e", dusk)}>
              PARCEL 0417 · 80 AC
            </Label>
          </g>
        </g>
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Shot 5 · The table. Grace under the kitchen bulb, heads bowed over a worn
// Bible. The book fades, the bulb dies, and the same bowed heads are lit from
// below by phones, more and more of them out into the dark.
// ---------------------------------------------------------------------------

const TABLE_TOP = poly([[470, 556], [1130, 556], [1270, 648], [330, 648]]);
const TABLE_FRONT = poly([[330, 648], [1270, 648], [1270, 672], [330, 672]]);
const TABLE_LEGS = "M 352 672 H 384 V 900 H 352 Z M 1216 672 H 1248 V 900 H 1216 Z";
const BIBLE = {
  cover: "M 742 587 Q 738 586 737 590 L 727 607 Q 725 611 730 611 L 870 611 Q 875 611 873 607 L 863 590 Q 862 586 858 587 Z",
  pages: "M 730 611 H 870 L 868 621 H 732 Z",
  back: "M 729 620 H 871 L 870 624 H 730 Z",
  cross: "M 800 591 V 606 M 791 595.5 H 809",
  ribbon: "M 842 620 L 846 642 L 851 637 L 849 620 Z",
};
// Seated figures from the waist up; origin where the body meets the table.
const FIG = {
  front: { body: "M -52 2 C -52 -28 -48 -52 -30 -60 L -11 -66 L 11 -66 L 30 -60 C 48 -52 52 -28 52 2 Z", head: [0, -92], r: 20, bow: [0, 16], neck: [0, -60] },
  back: { body: "M -66 2 C -66 -40 -60 -66 -38 -74 L -14 -80 L 14 -80 L 38 -74 C 60 -66 66 -40 66 2 Z", head: [0, -104], r: 23, bow: [0, 20], neck: [0, -72] },
  side: { body: "M -30 2 C -34 -36 -28 -62 -8 -72 L 10 -72 C 22 -62 30 -40 34 2 Z", head: [8, -94], r: 20, bow: [16, 12], neck: [0, -66] },
};
const FAMILY = [
  { kind: "front", x: 610, y: 556, s: 0.94, at: 0.43 },
  { kind: "front", x: 800, y: 552, s: 1.0, at: 0.4 },
  { kind: "front", x: 990, y: 556, s: 0.92, at: 0.46 },
  { kind: "side", x: 372, y: 622, s: 1.06, facing: 1, at: 0.48 },
  { kind: "side", x: 1228, y: 622, s: 1.06, facing: -1, at: 0.44 },
  { kind: "back", x: 585, y: 800, s: 1.55, at: 0.5 },
  { kind: "back", x: 1015, y: 800, s: 1.55, at: 0.47 },
];
// Everyone else, out in the dark beyond the table: three rows of people
// bowed over phones, then just the glow of phones further off.
const CROWD = (() => {
  const r = rng(616);
  const rows = [
    { y: 500, s: 0.8, n: 6, skip: 330 },
    { y: 464, s: 0.62, n: 9, skip: 0 },
    { y: 440, s: 0.48, n: 12, skip: 0 },
  ];
  const out = [];
  rows.forEach((row, ri) => {
    for (let i = 0; i < row.n; i++) {
      const x = 40 + ((i + 0.5) * 1520) / row.n + (r() - 0.5) * 50;
      if (Math.abs(x - 800) < row.skip) continue;
      out.push({
        kind: "front", x, y: row.y + (r() - 0.5) * 8, s: row.s * (0.9 + r() * 0.22), bow: 0.75 + r() * 0.35,
        at: 0.5 + (Math.abs(x - 800) / 800) * 0.2 + ri * 0.06 + r() * 0.04,
      });
    }
  });
  for (let i = 0; i < 30; i++) {
    const x = 30 + r() * 1540;
    out.push({ dots: true, x, y: 402 + r() * 26, s: 0.18 + r() * 0.16, at: 0.68 + (Math.abs(x - 800) / 800) * 0.12 + r() * 0.08 });
  }
  return out.sort((a, b) => a.y - b.y);
})();

function Sitter({ f, bow, ink, warm, glow }) {
  const F = FIG[f.kind];
  const hx = F.head[0] + F.bow[0] * bow;
  const hy = F.head[1] + F.bow[1] * bow;
  const shape = (color, dy, key, opacity = 1) => (
    <g key={key} fill={color} opacity={opacity} transform={dy ? `translate(0 ${dy})` : undefined}>
      <path d={F.body} />
      <path d={`M ${F.neck[0]} ${F.neck[1]} L ${f1(hx)} ${f1(hy)}`} stroke={color} strokeWidth={17} strokeLinecap="round" />
      <circle cx={f1(hx)} cy={f1(hy)} r={F.r} />
    </g>
  );
  const facing = f.facing || 1;
  const side = f.kind === "side";
  return (
    <g transform={`translate(${f1(f.x)} ${f1(f.y)}) scale(${(f.s * facing).toFixed(3)} ${f.s.toFixed(3)})`}>
      {glow > 0 && f.kind === "back" && (
        <circle cx={0} cy={hy + 6} r={96} fill="url(#rp-tb-halo)" opacity={glow} />
      )}
      {warm > 0.01 && shape(C.warm, -2.5, "w", 0.6 * warm)}
      {shape(ink, 0, "k")}
      {glow > 0 && f.kind !== "back" && (
        <g opacity={glow}>
          {/* Faceless: just the underside of the head lit cold */}
          <circle cx={f1(hx)} cy={f1(hy)} r={F.r} fill="url(#rp-tb-head)" />
          <path d={F.body} fill="url(#rp-tb-chest)" />
          <circle cx={side ? 40 : 0} cy={side ? -58 : -16} r={side ? 46 : 54} fill="url(#rp-tb-phone)" />
          {side
            ? <rect x={36} y={-80} width={4} height={26} rx={1} fill={C.coldHot} transform="rotate(-24 38 -67)" />
            : (
              <g>
                <rect x={-10} y={-40} width={20} height={28} rx={3} fill={ink} />
                <path d="M -8 -40.5 H 8" stroke={C.coldHot} strokeWidth={2} strokeLinecap="round" />
                <ellipse cx={-12} cy={-16} rx={8} ry={10} fill={ink} />
                <ellipse cx={12} cy={-16} rx={8} ry={10} fill={ink} />
              </g>
            )}
        </g>
      )}
    </g>
  );
}

function TableShot({ q, local }) {
  const bible = 1 - smooth(seg(q, 0.27, 0.42));
  // The bulb gutters and dies.
  const fl = seg(q, 0.3, 0.38);
  const bulb = q < 0.3 ? 1 : q > 0.38 ? 0 : noise(Math.floor(local * 18)) < 0.55 - fl * 0.5 ? 0.9 - fl * 0.4 : 0.08;
  const phone = smooth(seg(q, 0.36, 0.43));
  const sway = Math.sin(local * 0.9) * 3;
  const ink = "#070605";
  const wall = mixColor("#0a0a0c", "#2a1c12", bulb);
  return (
    <g>
      <defs>
        <radialGradient id="rp-tb-bulb">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.85" />
          <stop offset="0.3" stopColor={C.warm} stopOpacity="0.3" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rp-tb-cone" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.warmHot} stopOpacity="0.28" />
          <stop offset="1" stopColor={C.warm} stopOpacity="0.03" />
        </linearGradient>
        <radialGradient id="rp-tb-phone">
          <stop offset="0" stopColor={C.coldHot} stopOpacity="0.75" />
          <stop offset="0.3" stopColor={C.cold} stopOpacity="0.25" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rp-tb-head" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0.45" stopColor={C.cold} stopOpacity="0" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0.6" />
        </linearGradient>
        <linearGradient id="rp-tb-chest" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.14" />
          <stop offset="0.55" stopColor={C.cold} stopOpacity="0" />
        </linearGradient>
        <radialGradient id="rp-tb-halo">
          <stop offset="0.25" stopColor={C.cold} stopOpacity="0.5" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rp-tb-table" gradientUnits="userSpaceOnUse" x1="0" y1="556" x2="0" y2="672">
          <stop offset="0" stopColor={mixColor("#0b0b0d", "#4a2f1a", bulb)} />
          <stop offset="1" stopColor={mixColor("#060607", "#6a4426", bulb)} />
        </linearGradient>
      </defs>
      <rect x={EXT.x} y={EXT.y} width={EXT.w} height={EXT.h} fill={mixColor("#030304", "#0d0907", bulb)} />
      <g transform={`translate(800 560) scale(${lerp(1.2, 1.3, smooth(clamp(q))).toFixed(4)}) translate(-800 -560)`}>
        <rect x={EXT.x} y={EXT.y} width={EXT.w} height={560 - EXT.y} fill={wall} opacity={0.9} />
        <ellipse cx={800} cy={420} rx={760} ry={330} fill="url(#rp-tb-bulb)" opacity={0.55 * bulb} />

        {/* Window at the back: only the cold out there. The room goes as the
            dark fills up. */}
        <g opacity={1 - smooth(seg(q, 0.5, 0.68))}>
          <rect x={1190} y={250} width={150} height={190} fill="#101a24" />
          <rect x={1190} y={250} width={150} height={190} fill="none" stroke={mixColor("#050505", "#1c140d", bulb)} strokeWidth={8} />
          <path d="M 1265 250 V 440 M 1190 345 H 1340" stroke={mixColor("#050505", "#1c140d", bulb)} strokeWidth={5} />
        </g>

        {/* The crowd that fills the dark */}
        {CROWD.map((c, i) => {
          const k = smooth(seg(q, c.at, c.at + 0.05));
          if (k <= 0) return null;
          if (c.dots) {
            return <circle key={i} cx={f1(c.x)} cy={f1(c.y)} r={f1(44 * c.s)} fill="url(#rp-tb-phone)" opacity={k} />;
          }
          return <Sitter key={i} f={c} bow={c.bow} ink={ink} warm={0} glow={k} />;
        })}

        {/* The bulb */}
        <path d={`M 800 -1800 V ${f1(205)} L ${f1(800 + sway)} 214`} stroke="#14100c" strokeWidth={2} fill="none" />
        <path d={`M ${f1(780 + sway)} 214 H ${f1(820 + sway)} L ${f1(848 + sway)} 246 H ${f1(752 + sway)} Z`} fill="#16110c" />
        <ellipse cx={f1(800 + sway)} cy={250} rx={13} ry={9} fill={mixColor("#2a2520", C.warmHot, bulb)} />
        <path d={`M ${f1(760 + sway)} 248 L ${f1(840 + sway)} 248 L 1210 648 L 390 648 Z`} fill="url(#rp-tb-cone)" opacity={bulb} />

        {/* Far side and the two ends */}
        {FAMILY.filter((f) => f.kind !== "back").map((f, i) => (
          <Sitter key={i} f={f} bow={1} ink={ink} warm={bulb} glow={smooth(seg(q, f.at, f.at + 0.05))} />
        ))}

        {/* The table and what lies on it */}
        <path d={TABLE_TOP} fill="url(#rp-tb-table)" />
        <path d={TABLE_FRONT} fill={mixColor("#050505", "#2f1d10", bulb)} />
        <path d={TABLE_LEGS} fill="#050404" />
        <ellipse cx={800} cy={604} rx={230} ry={36} fill={C.warmHot} opacity={0.18 * bulb} />
        <g opacity={bible}>
          <ellipse cx={802} cy={622} rx={84} ry={8} fill="#000" opacity={0.4} />
          <path d={BIBLE.back} fill="#120c08" />
          <path d={BIBLE.pages} fill={mixColor("#3a3328", "#d9c9a2", bulb)} />
          <path d="M 734 614.5 H 866 M 734 617.5 H 866" stroke="#000" strokeOpacity={0.18} strokeWidth={0.8} />
          <path d={BIBLE.cover} fill={mixColor("#0c0907", "#2a1a10", bulb)} />
          <path d={BIBLE.cover} fill="none" stroke={mixColor("#120c08", "#5a3a22", bulb)} strokeWidth={1.2} />
          <path d={BIBLE.cross} stroke={mixColor("#2a2018", "#c9a35a", bulb)} strokeWidth={2.2} strokeLinecap="round" />
          <path d={BIBLE.ribbon} fill="#6e2418" />
        </g>
        {phone > 0 && (
          <g opacity={phone}>
            <ellipse cx={800} cy={600} rx={150} ry={30} fill="url(#rp-tb-phone)" />
            <path d="M 778 590 L 822 590 L 828 606 L 772 606 Z" fill="#05070a" />
            <path d="M 781 592 L 819 592 L 824 604 L 776 604 Z" fill={C.coldHot} opacity={0.85 + 0.15 * Math.sin(local * 3)} />
          </g>
        )}
        {FAMILY.filter((f) => f.kind !== "back").map((f, i) => {
          const g = smooth(seg(q, f.at, f.at + 0.05));
          if (g <= 0 || f.kind === "side") return null;
          return <ellipse key={i} cx={f.x} cy={f.y + 18} rx={90 * f.s} ry={16} fill="url(#rp-tb-phone)" opacity={0.5 * g} />;
        })}

        {/* Near side, backs to us */}
        {FAMILY.filter((f) => f.kind === "back").map((f, i) => (
          <Sitter key={i} f={f} bow={1} ink={ink} warm={bulb} glow={smooth(seg(q, f.at, f.at + 0.05))} />
        ))}
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// Shot 6 · The vine. A tomato plant on a stake in time-lapse: days flicker
// past, the fruit swells green, ripens red, then slumps, rots and drops.
// Nobody comes to pick it.
// ---------------------------------------------------------------------------

const VN = { ground: 748 };
const VN_STEM = "M 792 770 C 774 700 814 650 798 590 C 784 530 818 480 804 420 C 792 362 820 312 808 236";
const VN_LEAVES = [
  { x: 806, y: 300, a: -0.55, len: 150, side: 1 },
  { x: 800, y: 372, a: -2.5, len: 168, side: -1 },
  { x: 807, y: 456, a: -0.25, len: 182, side: 1 },
  { x: 796, y: 540, a: -2.8, len: 160, side: -1 },
  { x: 802, y: 622, a: 0.1, len: 150, side: 1 },
  { x: 808, y: 252, a: -2.0, len: 110, side: -1 },
  { x: 810, y: 246, a: -1.0, len: 96, side: 1 },
];
const VN_FRUIT = [
  { x: 694, y: 452, r: 34, stem: [722, 394], d: 0.0, drop: 0.7 },
  { x: 744, y: 476, r: 27, stem: [748, 416], d: 0.03, drop: 0.77 },
  { x: 904, y: 548, r: 38, stem: [882, 486], d: 0.015, drop: 0.73 },
  { x: 856, y: 568, r: 29, stem: [852, 504], d: 0.045, drop: 0.81 },
  { x: 724, y: 676, r: 31, stem: [748, 616], d: 0.025, drop: 0.66 },
];

function leafPath(x, y, a, len, side, droop) {
  let rib = `M ${f1(x)} ${f1(y)}`;
  let blades = "";
  let px = x;
  let py = y;
  let ang = a;
  const n = 6;
  const blade = (bx, by, dir, l, w) => {
    const ux = Math.cos(dir);
    const uy = Math.sin(dir);
    const ex = bx + ux * l;
    const ey = by + uy * l;
    return `M ${f1(bx)} ${f1(by)} Q ${f1(bx + ux * l * 0.5 - uy * w)} ${f1(by + uy * l * 0.5 + ux * w)} ${f1(ex)} ${f1(ey)} Q ${f1(bx + ux * l * 0.5 + uy * w)} ${f1(by + uy * l * 0.5 - ux * w)} ${f1(bx)} ${f1(by)} Z `;
  };
  for (let i = 1; i <= n; i++) {
    ang += side * (0.07 + 0.16 * droop);
    px += Math.cos(ang) * (len / n);
    py += Math.sin(ang) * (len / n);
    rib += ` L ${f1(px)} ${f1(py)}`;
    if (i === 2 || i === 4) {
      const l = len * (i === 2 ? 0.3 : 0.26);
      const spread = 1.0 - 0.45 * droop;
      blades += blade(px, py, ang - spread, l, l * 0.36);
      blades += blade(px, py, ang + spread, l, l * 0.36);
    }
  }
  blades += blade(px, py, ang, len * 0.3, len * 0.12);
  return { rib, blades };
}

function VineShot({ q, len, local }) {
  // Days flash by; later the days themselves run cold.
  const day = 0.5 - 0.5 * Math.cos(local * 2 * Math.PI * 1.15);
  const season = smooth(seg(q, 0.35, 1));
  const amp = 1 - 0.75 * seg(q, 0.55, 1);
  const light = day * amp;
  const lit = 0.3 + 0.7 * light;
  const zoom = lerp(1.24, 1.32, smooth(clamp(q)));
  const skyDay = mixColor("#9a7a58", "#4c5a66", season);
  const skyTop = mixColor("#03050a", mixColor("#3a3e4a", "#1a2028", season), light);
  const skyLow = mixColor("#0a0f16", skyDay, light);
  const wilt = smooth(seg(q, 0.45, 0.95));
  const leafCol = mixColor(mixColor("#36532b", "#5e5629", wilt), "#0c120b", 1 - lit);
  const stemCol = mixColor("#24361c", "#3a3420", wilt);
  const glow = smooth(seg(q, 0.55, 1));
  const shadowA = lerp(-1.1, 1.1, frac(local * 1.15 - 0.25));

  return (
    <g>
      <defs>
        <linearGradient id="rp-vn-sky" gradientUnits="userSpaceOnUse" x1="0" y1="-300" x2="0" y2="640">
          <stop offset="0" stopColor={skyTop} />
          <stop offset="1" stopColor={skyLow} />
        </linearGradient>
        <radialGradient id="rp-vn-rot">
          <stop offset="0" stopColor="#140a04" stopOpacity="0.85" />
          <stop offset="0.6" stopColor="#140a04" stopOpacity="0.4" />
          <stop offset="1" stopColor="#140a04" stopOpacity="0" />
        </radialGradient>
        <radialGradient id="rp-vn-glow">
          <stop offset="0" stopColor={C.cold} stopOpacity="0.5" />
          <stop offset="1" stopColor={C.cold} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rp-vn-soil" gradientUnits="userSpaceOnUse" x1="0" y1="640" x2="0" y2="900">
          <stop offset="0" stopColor={mixColor("#0c0a08", "#2c2016", light * 0.8)} />
          <stop offset="1" stopColor="#040302" />
        </linearGradient>
      </defs>
      <rect x={EXT.x} y={EXT.y} width={EXT.w} height={2460} fill="url(#rp-vn-sky)" />
      <g transform={`translate(800 470) scale(${zoom.toFixed(4)}) translate(-800 -524)`}>
        <ellipse cx={1180} cy={636} rx={700} ry={160} fill="url(#rp-vn-glow)" opacity={glow} />
        <path d="M -400 640 Q -200 618 0 630 T 360 622 T 700 634 T 1080 620 T 1440 632 T 2000 624 V 700 H -400 Z" fill="#050605" />
        <rect x={EXT.x} y={640} width={EXT.w} height={2300} fill="url(#rp-vn-soil)" />
        <path d="M -400 748 Q 400 738 800 750 T 2000 744" stroke="#000" strokeOpacity={0.5} strokeWidth={3} fill="none" />

        {/* The stake and its shadow wheeling round with the sun */}
        <path d={`M 806 ${VN.ground} L ${f1(806 + Math.sin(shadowA) * 420)} ${f1(VN.ground + 26)}`} stroke="#000" strokeOpacity={0.5 * light} strokeWidth={14} strokeLinecap="round" />
        <rect x={798} y={196} width={16} height={566} fill={mixColor("#1a120b", "#5c4128", light * 0.7)} />
        {[300, 432, 562].map((y) => <path key={y} d={`M 790 ${y} q 16 6 32 0`} stroke="#8a7a5a" strokeOpacity={0.6} strokeWidth={2.5} fill="none" />)}

        {/* The plant */}
        <path d={VN_STEM} stroke={stemCol} strokeWidth={9} fill="none" strokeLinecap="round" />
        {VN_LEAVES.map((lf, i) => {
          const sway = Math.sin(local * 2 * Math.PI * 1.15 + i) * 0.08 * (1 - wilt);
          const { rib, blades } = leafPath(lf.x, lf.y, lf.a + sway + lf.side * 0.5 * wilt, lf.len * (1 - 0.1 * wilt), lf.side, wilt);
          return (
            <g key={i}>
              <path d={blades} fill={leafCol} />
              <path d={rib} stroke={stemCol} strokeWidth={3} fill="none" strokeLinecap="round" />
            </g>
          );
        })}
        {VN_FRUIT.map((fr, i) => {
          const grow = ease.out(seg(q, 0.02 + fr.d, 0.3 + fr.d));
          const ripe = seg(q, 0.28 + fr.d, 0.48 + fr.d);
          const rot2 = seg(q, 0.5 + fr.d, 0.72 + fr.d);
          const r = fr.r * (0.12 + 0.88 * grow);
          const base = rot2 > 0
            ? mixColor(mixColor("#c2402a", "#6a4122", rot2), "#3a2414", seg(q, 0.72 + fr.d, 0.95))
            : ripe < 0.5 ? mixColor("#5c7a33", "#d9822e", ripe * 2) : mixColor("#d9822e", "#c2402a", ripe * 2 - 1);
          const col = mixColor("#050403", base, lit);
          const rx = r * (1.06 + 0.14 * rot2);
          const ry = r * (0.94 - 0.24 * rot2);
          // Drop: free fall to the soil, a bounce, then it lies there.
          const t = (q - fr.drop) * len;
          let cx = fr.x;
          let cy = fr.y + r * 0.1 * rot2;
          let squash = 1;
          if (t > 0) {
            const floor = VN.ground + 8 - ry;
            const fallT = Math.sqrt((2 * Math.max(1, floor - cy)) / 2600);
            if (t < fallT) cy += 1300 * t * t;
            else {
              const b = t - fallT;
              cy = floor - Math.max(0, Math.sin(Math.min(b, 0.2) / 0.2 * Math.PI)) * 14 * Math.exp(-b * 6);
              squash = 1 - 0.22 * smooth(seg(b, 0, 0.4));
              cx += 18 * smooth(seg(b, 0, 0.6)) * (i % 2 ? 1 : -1);
            }
          }
          const attached = t <= 0;
          return (
            <g key={i}>
              {attached && <path d={`M ${fr.stem[0]} ${fr.stem[1]} Q ${f1((fr.stem[0] + cx) / 2)} ${f1(fr.stem[1] + 6)} ${f1(cx)} ${f1(cy - ry + 2)}`} stroke={stemCol} strokeWidth={3} fill="none" />}
              <ellipse cx={f1(cx)} cy={f1(cy + ry * (1 - squash))} rx={f1(rx * (1 + (1 - squash) * 0.6))} ry={f1(ry * squash)} fill={col} />
              {rot2 > 0 && (
                <g opacity={rot2}>
                  <ellipse cx={f1(cx + rx * 0.34)} cy={f1(cy + ry * 0.12)} rx={f1(rx * 0.62)} ry={f1(ry * 0.66 * squash)} fill="url(#rp-vn-rot)" />
                  <path d={`M ${f1(cx - rx * 0.1)} ${f1(cy - ry * 0.2)} q ${f1(rx * 0.3)} ${f1(ry * 0.25)} ${f1(rx * 0.55)} ${f1(ry * 0.05)}`} stroke="#1a0e06" strokeWidth={2} fill="none" strokeLinecap="round" opacity={0.6} />
                </g>
              )}
              <ellipse cx={f1(cx - rx * 0.35)} cy={f1(cy - ry * 0.4)} rx={f1(rx * 0.22)} ry={f1(ry * 0.12)} fill="#fff4e0" opacity={0.35 * (1 - rot2) * grow * (0.3 + 0.7 * light)} />
              <path d={`M ${f1(cx)} ${f1(cy - ry * squash)} l -9 -3 m 9 3 l 8 -4 m -8 4 l 1 -9 m -1 9 l -6 5 m 6 -5 l 7 4`} stroke={stemCol} strokeWidth={2.5} strokeLinecap="round" />
            </g>
          );
        })}
      </g>
    </g>
  );
}

// ---------------------------------------------------------------------------
// The montage
// ---------------------------------------------------------------------------

const SHOTS = [
  { at: 0, Shot: LineShot },
  { at: 0.1, Shot: BenchShot },
  { at: 0.3, Shot: CoffinShot },
  { at: 0.48, Shot: FieldShot },
  { at: 0.665, Shot: TableShot },
  { at: 0.835, Shot: VineShot },
];

export default function Replaced({ p, t, local, dur }) {
  const half = XF / 2 / dur;
  return (
    <g>
      {SHOTS.map(({ at: a, Shot }, i) => {
        const end = i < SHOTS.length - 1 ? SHOTS[i + 1].at : 1;
        if (i > 0 && p < a - half) return null;
        if (i < SHOTS.length - 1 && p > end + half) return null;
        const alpha = i === 0 ? 1 : smooth(seg(p, a - half, a + half));
        return (
          <g key={i} opacity={alpha}>
            <Shot q={(p - a) / (end - a)} len={(end - a) * dur} local={local} t={t} />
          </g>
        );
      })}
    </g>
  );
}
